// When a screen gets the phone layout (docs/mobile-pwa-plan.md "When phones get the new screens").
//
//   phone app (this device)  = width < 768  AND not "Use desktop layout" here
//                              AND (GET /features mobile.enabled OR "phone preview" here)
//   phone layout (this page) = phone app AND the page is switched on (mobile.pages; preview =
//                              every page this build has) AND this build has its phone screen
//
// Shop screens are all 1920 px or wider (checked 2026-10-08: shipping Pi 1920x1080, laser Pi
// 2560x1440, shop TV 1920x1080; the camera kiosks run their own app), so they never qualify.
// Device settings live in localStorage (per device, per browser; the home-screen app has its own).
import { BUILT_PAGE_KEYS, PHONE_PAGES, pageKeyForPath } from './pages';

export const PHONE_MAX_WIDTH = 768; // phone = narrower than this (App.jsx's existing rule)

export const PREF_PREVIEW = 'jobq.phone.preview'; // '1' = try phone pages before they are switched on
export const PREF_LAYOUT = 'jobq.phone.layout'; // 'desktop' = never the phone layout here

export function isPhoneWidth(width) {
  return typeof width === 'number' && width > 0 && width < PHONE_MAX_WIDTH;
}

function storage() {
  try {
    return window.localStorage;
  } catch (e) {
    return null;
  }
}

export function readPhonePrefs() {
  const ls = storage();
  let preview = false;
  let forceDesktop = false;
  try {
    preview = !!ls && ls.getItem(PREF_PREVIEW) === '1';
    forceDesktop = !!ls && ls.getItem(PREF_LAYOUT) === 'desktop';
  } catch (e) { /* blocked storage = defaults */ }
  return { preview, forceDesktop };
}

export function writePhonePref(name, on) {
  const ls = storage();
  if (!ls) return;
  try {
    if (name === 'preview') {
      if (on) ls.setItem(PREF_PREVIEW, '1');
      else ls.removeItem(PREF_PREVIEW);
    } else if (name === 'forceDesktop') {
      if (on) ls.setItem(PREF_LAYOUT, 'desktop');
      else ls.removeItem(PREF_LAYOUT);
    }
  } catch (e) { /* private mode: the setting lasts for this page only */ }
}

/**
 * `?phone=` on any JobQ link changes this device's setting (handy on prod, where nothing is
 * switched on yet): preview | nopreview | desktop | auto. Returns the pref patch, or null.
 */
export function phoneParamPatch(search) {
  let v = null;
  try {
    v = new URLSearchParams(search || '').get('phone');
  } catch (e) {
    return null;
  }
  switch ((v || '').toLowerCase()) {
    case 'preview': return { preview: true };
    case 'nopreview': return { preview: false };
    case 'desktop': return { forceDesktop: true };
    case 'auto': return { forceDesktop: false };
    default: return null;
  }
}

/** The page keys switched on for phones on this device. */
export function activePageKeys(mobileFeature, prefs) {
  const built = new Set(BUILT_PAGE_KEYS);
  if (prefs && prefs.preview) return built;
  const on = mobileFeature && mobileFeature.enabled && Array.isArray(mobileFeature.pages) ? mobileFeature.pages : [];
  const keys = new Set(on.filter((k) => built.has(k)));
  // A page that rides with another (the pallet check rides with Scan) is on wherever that one is.
  for (const p of PHONE_PAGES) {
    if (p.rides && p.built && keys.has(p.rides)) keys.add(p.key);
  }
  return keys;
}

/** Device level: should this device use the phone app at all? */
export function phoneAppActive({ width, mobileFeature, prefs }) {
  if (!isPhoneWidth(width)) return false;
  if (prefs && prefs.forceDesktop) return false;
  return Boolean((mobileFeature && mobileFeature.enabled) || (prefs && prefs.preview));
}

/** Page level: does this path render in the phone layout? */
export function phoneLayoutFor(pathname, { width, mobileFeature, prefs }) {
  if (!phoneAppActive({ width, mobileFeature, prefs })) return false;
  const key = pageKeyForPath(pathname);
  return Boolean(key) && activePageKeys(mobileFeature, prefs).has(key);
}

// The current answer for code that runs outside React (utils/pinPrompt.js, App.jsx's admin
// PIN prompt). MobileModeProvider keeps it up to date.
let current = { phoneApp: false };

export function setPhoneAppSnapshot(snapshot) {
  current = { ...current, ...snapshot };
}

export function isPhoneAppNow() {
  return Boolean(current.phoneApp);
}
